import {
    BoundedContent,
    Box,
    Button,
    Column,
    FluidImage,
    GridContainer,
    H2,
    H3,
    Icon,
    Link,
    P,
    Row,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { FormattedReactMessage } from '@99designs/design-services-common';
import { SupportedLocale, __, __image } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { useIbpoBriefCreateURL } from '../../lib/useIbpoBriefCreateURL';
import { CloudinaryImage } from '../CloudinaryImage';
import { DesignLive, StandardReview } from './Popovers';

const StyledFluidImage = styled(FluidImage)`
    max-width: 133px;
    @media (min-width: ${tokensRaw.SwanBaseBreakpointLgStart}) {
        max-width: 166px;
    }
`;

export type HowItWorksProps = {
    hideCTA?: boolean;
    locale: SupportedLocale;
};

const LOCALES_WITHOUT_DESIGN_LIVE = ['fi-FI', 'da-DK', 'sv-SE', 'nb-NO'];

export function HowItWorks({ hideCTA = false, locale }: HowItWorksProps) {
    const isliveVideoCallUnavailable = LOCALES_WITHOUT_DESIGN_LIVE.includes(locale);
    const { pageData } = usePage();
    const briefCreateURL = useIbpoBriefCreateURL();
    const onClickTracking = () => {
        sendNavigationEvent({
            label: 'Content:How it works',
            eventDetail: `/design;${briefCreateURL};How it works;CreateAProject-CTA`,
            navigationDetail: 'CreateAProject-CTA',
            ...pageData,
        });
    };

    return (
        <BoundedContent
            backgroundColor="warm-white"
            paddingTop={{ xs: '8', md: '10' }}
            paddingBottom={{ xs: '9', md: '11' }}
            data-testid="how-it-works"
        >
            <Box paddingLeft={{ lg: '12' }} paddingRight={{ lg: '12' }}>
                <Box
                    marginBottom={{ xs: 8, md: 10 }}
                    textAlign="center"
                    data-testid="how-it-works-header"
                >
                    <H2>{__('The design process explained')}</H2>
                </Box>

                <GridContainer textAlign="center" data-testid="how-it-works-steps">
                    <Row as="ol">
                        <Column span={4} spanXs={12} as="li" data-testid="how-it-works-step-1">
                            <Box marginBottom={{ xs: '7', sm: '8', md: '7' }}>
                                <CloudinaryImage
                                    render={(props) => (
                                        <StyledFluidImage
                                            src={props.src}
                                            srcSet={props.srcSet}
                                            alt=""
                                            role="presentation"
                                        />
                                    )}
                                    imagePath={__image(
                                        'expert_services/HubPage/DesignProcess/how-it-works-image-step-1'
                                    )}
                                    width={166}
                                />
                                <H3 fontSkin="title-subsection" marginBottom="0" marginTop="0">
                                    {__('Complete your design brief')}
                                </H3>
                                <P marginBottom="0" marginTop="0">
                                    {__(
                                        'Fill out a quick, online form (your brief) with details about your design idea – think text, colors and images. Once you’re ready, hit submit.'
                                    )}
                                </P>
                            </Box>
                        </Column>
                        <Column span={4} spanXs={12} as="li" data-testid="how-it-works-step-2">
                            <Box marginBottom={{ xs: '7', sm: '8', md: '7' }}>
                                <CloudinaryImage
                                    render={(props) => (
                                        <StyledFluidImage
                                            src={props.src}
                                            srcSet={props.srcSet}
                                            alt=""
                                            role="presentation"
                                        />
                                    )}
                                    imagePath={__image(
                                        'expert_services/HubPage/DesignProcess/how-it-works-image-step-2'
                                    )}
                                    width={166}
                                />
                                <H3 fontSkin="title-subsection" marginBottom="0" marginTop="0">
                                    {__('Receive and review your design')}
                                </H3>
                                {isliveVideoCallUnavailable ? (
                                    <FormattedReactMessage
                                        message={__(
                                            'Now it’s time to review your design, collaborate with your designer and <StandardReview>share your feedback online</StandardReview>.',
                                            {
                                                _context:
                                                    'How it works description with StandardReview popover explaining the reviewing option further',
                                            }
                                        )}
                                        replacements={{
                                            StandardReview: <StandardReview> </StandardReview>,
                                        }}
                                    />
                                ) : (
                                    <FormattedReactMessage
                                        message={__(
                                            'Now it’s time to review your design, collaborate with your designer and share your feedback – via <DesignLive>a live video call</DesignLive> or <StandardReview>review online</StandardReview>',
                                            {
                                                _context:
                                                    'How it works description with DesignLive and StandardReview popovers explaining these reviewing options further',
                                            }
                                        )}
                                        replacements={{
                                            DesignLive: <DesignLive> </DesignLive>,
                                            StandardReview: <StandardReview> </StandardReview>,
                                        }}
                                    />
                                )}
                            </Box>
                        </Column>
                        <Column span={4} spanXs={12} as="li" data-testid="how-it-works-step-3">
                            <Box marginBottom={{ xs: '8', md: '7' }}>
                                <CloudinaryImage
                                    render={(props) => (
                                        <StyledFluidImage
                                            src={props.src}
                                            srcSet={props.srcSet}
                                            alt=""
                                            role="presentation"
                                        />
                                    )}
                                    imagePath={__image(
                                        'expert_services/HubPage/DesignProcess/how-it-works-image-step-3'
                                    )}
                                    width={166}
                                />
                                <H3 fontSkin="title-subsection" marginBottom="0" marginTop="0">
                                    {__('Approve your design')}
                                </H3>
                                <P marginBottom="0" marginTop="0">
                                    {__(
                                        'Once you’re happy, your finished design will be loaded into your VistaPrint account to view (and print) at any time.'
                                    )}
                                </P>
                            </Box>
                        </Column>
                    </Row>
                </GridContainer>
                {!hideCTA && (
                    <Box textAlign="center" data-testid="how-it-works-create-project">
                        <Button
                            skin="primary"
                            render={(props) => (
                                <Link
                                    to={briefCreateURL}
                                    className={props.className}
                                    onClick={onClickTracking}
                                    data-testid="how-it-works-create-project-link"
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            {__('Create a project')}
                            <Icon iconType="arrowRight" />
                        </Button>
                    </Box>
                )}
            </Box>
        </BoundedContent>
    );
}
